/**
 * Payment Detail Modal Component
 * Displays detailed payment information with timeline
 *
 * @author SAUBHAGYA Development Team
 * @since Story 4-5
 *
 * @description
 * Modal component showing:
 * - Payment summary (ID, status, amount, dates)
 * - Payment details (batch, gaushala, gateway info)
 * - Status timeline with transitions
 * - Gateway response (collapsible)
 *
 * @example
 * ```tsx
 * const [paymentId, setPaymentId] = useState<number | null>(null);
 *
 * <PaymentDetailModal
 *   paymentId={paymentId}
 *   open={paymentId !== null}
 *   onOpenChange={(open) => !open && setPaymentId(null)}
 * />
 * ```
 */

import React, { useState } from 'react';
import { format } from 'date-fns';
import { X, Clock, CheckCircle2, XCircle, Calendar, Building2, CreditCard, Hash, AlertCircle, Eye } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { usePaymentDetail } from '@/hooks/usePayments';
import { PaymentStatusBadge } from '../badges/PaymentStatusBadge';
import { bankAccountService, BankAccountResponse } from '@/services/gaushala/bankAccountService';
import { API_ENDPOINTS, fetchWithAuth } from '@/lib/apiConfig';

interface PaymentDetailModalProps {
  paymentId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Format currency amount
 */
function formatCurrency(amount: number, currency: string = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date time
 */
function formatDateTime(dateString: string): string {
  return format(new Date(dateString), 'PPp');
}

/**
 * Format date only
 */
function formatDate(dateString: string): string {
  return format(new Date(dateString), 'PP');
}

/**
 * Loading skeleton for modal content
 */
function PaymentDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Separator />
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-20" />
      </div>
      <Separator />
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-32" />
      </div>
    </div>
  );
}

/**
 * Payment Detail Modal Component
 */
export function PaymentDetailModal({
  paymentId,
  open,
  onOpenChange,
}: PaymentDetailModalProps) {
  const { data: payment, isLoading, error } = usePaymentDetail(paymentId, open);

  const [isGatewayResponseOpen, setIsGatewayResponseOpen] = React.useState(false);
  const [bankAccount, setBankAccount] = useState<BankAccountResponse | null>(null);
  const [bankAccountLoading, setBankAccountLoading] = useState(false);
  const [bankAccountError, setBankAccountError] = useState<string | null>(null);

  // Fetch bank account when payment data loads
  React.useEffect(() => {
    const fetchBankAccount = async () => {
      if (!payment?.gaushalaId) return;

      try {
        setBankAccountLoading(true);
        setBankAccountError(null);

        const response = await fetchWithAuth(
          API_ENDPOINTS.gaushala.getBankAccount(payment.gaushalaId)
        );

        if (!response.ok) {
          if (response.status === 404) {
            setBankAccountError('No bank account found');
          } else {
            setBankAccountError('Failed to load bank account details');
          }
          setBankAccount(null);
          return;
        }

        const data = await response.json();
        setBankAccount(data.data || null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load bank account';
        setBankAccountError(errorMessage);
        setBankAccount(null);
      } finally {
        setBankAccountLoading(false);
      }
    };

    fetchBankAccount();
  }, [payment?.gaushalaId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Payment Details</span>
            {payment && <PaymentStatusBadge status={payment.status} size="lg" />}
          </DialogTitle>
          <DialogDescription>
            {payment
              ? `Payment ID: ${payment.id} - Batch ${payment.batchCode}`
              : 'Loading payment details...'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <PaymentDetailSkeleton />}

        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <AlertCircle className="h-5 w-5" />
                <p>Failed to load payment details. Please try again.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {payment && (
          <div className="space-y-6">
            {/* Payment Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Amount
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {formatCurrency(payment.amount, payment.currency)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Initiated
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{formatDateTime(payment.initiatedAt)}</p>
                  {payment.completedAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Completed: {formatDate(payment.completedAt)}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            <Separator />

            {/* Payment Details */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Payment Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Transaction ID:</span>
                  <p className="font-medium">{payment.transactionId}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Batch Code:</span>
                  <p className="font-medium">{payment.batchCode}</p>
                </div>
                <div>
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    Gaushala:
                  </span>
                  <p className="font-medium">{payment.gaushalaName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Gateway Type:</span>
                  <p className="font-medium">{payment.gatewayType}</p>
                </div>
                {payment.gatewayTransactionId && (
                  <div className="md:col-span-2">
                    <span className="text-muted-foreground">Gateway Transaction ID:</span>
                    <p className="font-mono text-xs mt-1">{payment.gatewayTransactionId}</p>
                  </div>
                )}
                {payment.failureReason && (
                  <div className="md:col-span-2">
                    <span className="text-muted-foreground text-red-600 dark:text-red-400">
                      Failure Reason:
                    </span>
                    <p className="text-red-800 dark:text-red-200 mt-1">{payment.failureReason}</p>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Bank Account Verification Section */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Bank Account Verification
              </h3>

              {bankAccountLoading && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <Skeleton className="h-6 w-48" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-40" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {bankAccountError && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    {bankAccountError}
                  </AlertDescription>
                </Alert>
              )}

              {bankAccount && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Verification Status Badge */}
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <span className="text-sm font-medium text-gray-700">
                          Bank Account Status
                        </span>
                        <Badge className="bg-green-100 text-green-800 gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Verified
                        </Badge>
                      </div>

                      {/* Account Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Account Holder</span>
                          <p className="font-medium">{bankAccount.accountHolderName}</p>
                        </div>

                        <div>
                          <span className="text-muted-foreground">Account Number</span>
                          <p className="font-mono text-base font-medium">
                            {bankAccount.maskedAccountNumber}
                          </p>
                        </div>

                        <div>
                          <span className="text-muted-foreground">Bank Name</span>
                          <p className="font-medium">{bankAccount.bankName}</p>
                        </div>

                        <div>
                          <span className="text-muted-foreground">IFSC Code</span>
                          <p className="font-mono font-medium">{bankAccount.ifscCode}</p>
                        </div>

                        {bankAccount.branchName && (
                          <div>
                            <span className="text-muted-foreground">Branch</span>
                            <p className="font-medium">{bankAccount.branchName}</p>
                          </div>
                        )}

                        {bankAccount.accountType && (
                          <div>
                            <span className="text-muted-foreground">Account Type</span>
                            <p className="font-medium">{bankAccount.accountType}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <Separator />

            {/* Timeline */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Status Timeline
              </h3>
              <div className="space-y-3">
                {payment.timeline && payment.timeline.length > 0 ? (
                  payment.timeline.map((event, index) => (
                    <Card key={index} className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {event.previousStatus && (
                                <Badge variant="outline" className="text-xs">
                                  {event.previousStatus}
                                </Badge>
                              )}
                              <span className="text-muted-foreground">→</span>
                              <Badge className="text-xs">{event.newStatus}</Badge>
                            </div>
                            {event.notes && (
                              <p className="text-sm text-muted-foreground mt-2">
                                {event.notes}
                              </p>
                            )}
                            {event.gatewayResponse && (
                              <details className="mt-2">
                                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                                  Gateway Response
                                </summary>
                                <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                                  {event.gatewayResponse}
                                </pre>
                              </details>
                            )}
                          </div>
                          <time className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(event.createdAt)}
                          </time>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No timeline events available.</p>
                )}
              </div>
            </div>

            {/* Gateway Response (Collapsible) */}
            {payment.gatewayResponse && (
              <>
                <Separator />
                <div>
                  <Button
                    variant="outline"
                    className="w-full mb-3"
                    onClick={() => setIsGatewayResponseOpen(!isGatewayResponseOpen)}
                  >
                    {isGatewayResponseOpen ? 'Hide' : 'Show'} Gateway Response
                  </Button>
                  {isGatewayResponseOpen && (
                    <Card>
                      <CardContent className="pt-4">
                        <pre className="text-xs bg-muted p-3 rounded overflow-x-auto max-h-64 overflow-y-auto">
                          {payment.gatewayResponse}
                        </pre>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PaymentDetailModal;
